import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";

/**
 * interfaces
 */
import { IPasswordValidatorFnOptions } from "./interfaces";

/**
 * validators
 */
import { AppValidators } from "../validators";
import { AppValidationErrorsModel } from "../models";

/**
 * Password validator class that provides comprehensive password validation.
 * Supports various password strength requirements and confirm password validation.
 * 
 * The validator uses default configuration options if none are provided, making it
 * easy to use with sensible defaults while allowing customization when needed.
 * 
 * @abstract
 * @example
 * ```typescript
 * // Use default options (minLength: 8, maxLength: 32, all strength checks enabled)
 * const passwordForm = new FormGroup({
 *   password: new FormControl('', [
 *     PasswordValidator.validator() // Uses defaultOptions automatically
 *   ])
 * });
 * 
 * // Override specific options while keeping others as defaults
 * const customPasswordForm = new FormGroup({
 *   password: new FormControl('', [
 *     PasswordValidator.validator({
 *       minLength: 12,        // Override default (8)
 *       maxLength: 50,        // Override default (32)
 *       atLeastOneSymbol: false // Override default (true)
 *       // Other options (atLeastOneUpper, atLeastOneLower, atLeastOneNumber) 
 *       // will use default values (true)
 *     })
 *   ])
 * });
 * 
 * // Password confirmation validation
 * const registrationForm = new FormGroup({
 *   password: new FormControl(''),
 *   confirmPassword: new FormControl('', [
 *     PasswordValidator.confirmValidatorFn()
 *   ])
 * });
 * ```
 */
export abstract class PasswordValidator {

    /**
     * Creates a password validator function with configurable options.
     * If no options are provided, uses the default configuration from `defaultOptions`.
     * 
     * @static
     * @param options - Optional configuration for password validation. If not provided,
     *                  uses `PasswordValidator.defaultOptions` which includes:
     *                  - minLength: 8, maxLength: 32
     *                  - atLeastOneUpper: true, atLeastOneLower: true
     *                  - atLeastOneNumber: true, atLeastOneSymbol: true
     * @returns A ValidatorFn that validates password strength
     * @example
     * ```typescript
     * // Use default options - no parameters needed
     * const passwordForm = new FormGroup({
     *   password: new FormControl('', [
     *     PasswordValidator.validator() // Automatically uses defaultOptions
     *   ])
     * });
     * 
     * // Override only specific options
     * const customForm = new FormGroup({
     *   password: new FormControl('', [
     *     PasswordValidator.validator({
     *       minLength: 12,        // Override default (8)
     *       atLeastOneSymbol: false // Override default (true)
     *       // Other options will use defaults
     *     })
     *   ])
     * });
     * ```
     */
    public static validator(options?: IPasswordValidatorFnOptions): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            
            // ES6 destructuring
            const { value = '' } = control;

            // void validation
            if (typeof value !== 'string' || value.trim().length === 0) return null;

            // ES6 destructuring - use provided options or fall back to defaults
            const { 
                atLeastOneLower,
                atLeastOneUpper,
                atLeastOneNumber,
                atLeastOneSymbol,
                minLength,
                maxLength,
                pattern,
            } = options ?? PasswordValidator.defaultOptions;

            // validate at least one lowercase letter
            if (atLeastOneLower) {
                const _errors = AppValidators.atLeastOneLower(control);
                if (_errors) return _errors;
            }

            // validate at least one uppercase letter
            if (atLeastOneUpper) {
                const _errors = AppValidators.atLeastOneUpper(control);
                if (_errors) return _errors;
            }

            // validate at least one number
            if (atLeastOneNumber) {
                const _errors = AppValidators.atLeastOneNumber(control);
                if (_errors) return _errors;
            }

            // validate at least one symbol
            if (atLeastOneSymbol) {
                const _errors = AppValidators.atLeastOneSymbol(control);
                if (_errors) return _errors;
            }

            // validate minimum length
            if (typeof minLength === 'number') {

                // validate minimum length
                const _minLengthFn = AppValidators.minLength(minLength);
                const _minLengthErrors = _minLengthFn(control);

                if (_minLengthErrors) return _minLengthErrors;
            }

            // validate maximum length
            if (typeof maxLength === 'number') {

                // validate maximum length
                const _maxLengthFn = AppValidators.maxLength(maxLength);
                const _maxLengthErrors = _maxLengthFn(control);

                if (_maxLengthErrors) return _maxLengthErrors;
            }

        
            // void validation
            return null;

        };
    };

    /**
     * Creates a validator function for password confirmation.
     * Validates that confirm password matches the original password.
     * 
     * @static
     * @returns A ValidatorFn that validates password confirmation
     * @example
     * ```typescript
     * const registrationForm = new FormGroup({
     *   password: new FormControl(''),
     *   confirmPassword: new FormControl('', [
     *     PasswordValidator.confirmValidatorFn()
     *   ])
     * });
     * ```
     */
    public static confirmValidatorFn(passwordControlName: string = 'password', confirmPasswordControlName: string = 'confirmPassword'): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            // ES6 destructuring
            const { [passwordControlName]: password, [confirmPasswordControlName]: confirmPassword } = (control as FormGroup).controls;

            // void validation
            if (confirmPassword.errors !== null) return null;

            // check if the password and confirm password do not match
            if (password.value !== confirmPassword.value) {

                const _errors = new AppValidationErrorsModel('doNotMatch', true, { key: 'FORM.GENERAL_FIELDS.CONFIRM_PASSWORD.DO_NOT_MATCH' });

                confirmPassword.setErrors(_errors);
                return null;

            }

            // clear the errors
            confirmPassword.setErrors(null);
            return null;
        }
        
        

    }
    
    /**
     * Default configuration options for password validation.
     * These options are automatically used when calling `validator()` without parameters.
     * 
     * @static
     * @example
     * ```typescript
     * // Use default options automatically
     * const passwordForm = new FormGroup({
     *   password: new FormControl('', [
     *     PasswordValidator.validator() // Uses these defaults:
     *     // minLength: 8, maxLength: 32
     *     // atLeastOneUpper: true, atLeastOneLower: true
     *     // atLeastOneNumber: true, atLeastOneSymbol: true
     *   ])
     * });
     * 
     * // Override specific options while keeping others as defaults
     * const customForm = new FormGroup({
     *   password: new FormControl('', [
     *     PasswordValidator.validator({
     *       ...PasswordValidator.defaultOptions, // Spread defaults
     *       minLength: 12,                      // Override minLength
     *       atLeastOneSymbol: false            // Override atLeastOneSymbol
     *     })
     *   ])
     * });
     * ```
     */
    public static defaultOptions: IPasswordValidatorFnOptions = {
        minLength: 8,
        maxLength: 32,
        atLeastOneLower: true,
        atLeastOneUpper: true,
        atLeastOneNumber: true,
        atLeastOneSymbol: true,
    };

}
